import {ThumbsupIcon, ThumbsdownIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useCallback, useState} from 'react'
import capitalize from 'lodash-es/capitalize'
import safeStorage from '@github-ui/safe-storage'
import {useFeedbackDialog} from './useFeedbackDialog'
import {useSessionContext} from '../../../contexts/SessionContext'
import {useCurrentUserOptional} from '../../../contexts/CurrentUserContext'
import styles from './SessionFeedbackButtons.module.css'
import {type RatingOption, thumbsDownIcon, thumbsUpIcon} from '@github-ui/user-feedback'

const FEEDBACK_STORAGE_KEY = 'COPILOT_CHAT_V2_RESPONSE_FEEDBACK'
const MAX_STORED_FEEDBACK_COUNT = 100

const ratingOptions: Array<RatingOption<'NEGATIVE' | 'POSITIVE'>> = [
  {
    name: 'Bad',
    value: 'NEGATIVE' as const,
    icon: thumbsDownIcon,
    color: 'bad',
  },
  {
    name: 'Good',
    value: 'POSITIVE' as const,
    icon: thumbsUpIcon,
    color: 'good',
  },
]

type Rating = (typeof ratingOptions)[number]['value']

interface StoredFeedback {
  rating: Rating
  scope: string
}

const feedbackStorage = safeStorage('localStorage', {
  throwQuotaErrorsOnSet: false,
  ttl: 1000 * 60 * 60 * 24,
})

function getStoredFeedback(): StoredFeedback[] {
  try {
    const parsed: unknown = JSON.parse(feedbackStorage.getItem(FEEDBACK_STORAGE_KEY) ?? '[]')
    if (!Array.isArray(parsed)) return []

    return parsed.filter(
      (item): item is StoredFeedback =>
        typeof item === 'object' &&
        item !== null &&
        'rating' in item &&
        (item.rating === 'POSITIVE' || item.rating === 'NEGATIVE') &&
        'scope' in item &&
        typeof item.scope === 'string',
    )
  } catch {
    return []
  }
}

function getStoredFeedbackRating(scope: string): Rating | null {
  return getStoredFeedback().find(item => item.scope === scope)?.rating ?? null
}

function storeFeedbackRating(scope: string, rating: Rating): void {
  const storedFeedback = getStoredFeedback().filter(item => item.scope !== scope)
  storedFeedback.push({rating, scope})
  feedbackStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(storedFeedback.slice(-MAX_STORED_FEEDBACK_COUNT)))
}

interface SessionFeedbackButtonsProps {
  feedbackSource?: string
}

export function SessionFeedbackButtons({feedbackSource}: SessionFeedbackButtonsProps = {}) {
  const {session} = useSessionContext()
  const currentUserLogin = useCurrentUserOptional()?.currentUser?.login
  const source = feedbackSource ?? `session-log:session_id=${session.id}`
  const feedbackScope = `${session.id}:${source}`

  const [feedbackState, setFeedbackState] = useState(() => ({
    rating: getStoredFeedbackRating(feedbackScope),
    scope: feedbackScope,
  }))

  if (feedbackState.scope !== feedbackScope) {
    setFeedbackState({
      rating: getStoredFeedbackRating(feedbackScope),
      scope: feedbackScope,
    })
  }

  const submittedFeedback = feedbackState.rating
  const {openFeedback, FeedbackDialog} = useFeedbackDialog(source, ratingOptions, currentUserLogin)
  const onClick = useCallback(
    (rating: Rating) => {
      setFeedbackState({rating, scope: feedbackScope}) // Feedback isn't submitted at this point, but we're optimistically updating the UI
      openFeedback(rating)
    },
    [feedbackScope, openFeedback],
  )

  const onFeedbackSubmitted = useCallback(
    (rating: Rating) => {
      storeFeedbackRating(feedbackScope, rating)
      setFeedbackState({rating, scope: feedbackScope})
    },
    [feedbackScope],
  )
  const onFeedbackDialogClose = useCallback(
    (reason: 'submit' | 'cancel' | 'dismiss') => {
      if (reason === 'cancel') {
        setFeedbackState({rating: null, scope: feedbackScope})
      }
    },
    [feedbackScope],
  )

  return (
    <div className={styles.container}>
      {submittedFeedback != null ? (
        <IconButton
          aria-label={`${capitalize(submittedFeedback)} feedback submitted`}
          icon={submittedFeedback === 'POSITIVE' ? ThumbsupIcon : ThumbsdownIcon}
          variant="invisible"
          size="small"
          inactive
          aria-disabled
        />
      ) : (
        <>
          <IconButton
            icon={ThumbsupIcon}
            aria-label="Good response"
            onClick={() => onClick('POSITIVE')}
            variant="invisible"
            size="small"
          />
          <IconButton
            icon={ThumbsdownIcon}
            aria-label="Bad response"
            onClick={() => onClick('NEGATIVE')}
            variant="invisible"
            size="small"
          />
        </>
      )}
      <FeedbackDialog onClose={onFeedbackDialogClose} onSubmitted={onFeedbackSubmitted} />
    </div>
  )
}
